import { lazy, Suspense } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router';

import { AppShell } from '@app/layouts/app-shell';
import { RequireAuth } from '@features/auth/guards/require-auth';
import { RequirePermission } from '@features/auth/guards/require-permission';
import { LoadingState } from '@shared/ui/states';
import { NotFoundPage } from './not-found.page';

/**
 * Rutas de la aplicacion.
 *
 * Cada pagina se carga de forma diferida (spec 52): un operario de almacen no
 * deberia descargar el modulo de reportes para registrar una recepcion. El
 * dashboard se importa igualmente diferido para que el chunk inicial contenga
 * solo el armazon y el enrutador.
 *
 * La estructura es deliberada: TODO cuelga de `RequireAuth` salvo el acceso.
 * Al reves -proteger ruta por ruta- basta olvidar una para dejar una pantalla
 * abierta, y ese olvido no produce ningun error visible.
 */

const LoginPage = lazy(async () => ({
  default: (await import('@features/auth/pages/login.page')).LoginPage,
}));

const ChangePasswordPage = lazy(async () => ({
  default: (await import('@features/auth/pages/change-password.page')).ChangePasswordPage,
}));

const DashboardPage = lazy(async () => ({
  default: (await import('@features/dashboard/pages/dashboard.page')).DashboardPage,
}));

const SystemStatusPage = lazy(async () => ({
  default: (await import('@features/system/pages/system-status.page')).SystemStatusPage,
}));

const DesignSystemPage = lazy(async () => ({
  default: (await import('@features/design-system/pages/design-system.page')).DesignSystemPage,
}));

const TransfersPage = lazy(async () => ({
  default: (await import('@features/transfers/pages/transfers.page')).TransfersPage,
}));
const TransferDetailPage = lazy(async () => ({
  default: (await import('@features/transfers/pages/transfer-detail.page')).TransferDetailPage,
}));
const DispatchesPage = lazy(async () => ({
  default: (await import('@features/dispatches/pages/dispatches.page')).DispatchesPage,
}));
const DispatchDetailPage = lazy(async () => ({
  default: (await import('@features/dispatches/pages/dispatch-detail.page')).DispatchDetailPage,
}));

const CountsPage = lazy(async () => ({
  default: (await import('@features/counts/pages/counts.page')).CountsPage,
}));
const CountDetailPage = lazy(async () => ({
  default: (await import('@features/counts/pages/count-detail.page')).CountDetailPage,
}));
const AdjustmentsPage = lazy(async () => ({
  default: (await import('@features/adjustments/pages/adjustments.page')).AdjustmentsPage,
}));
const AdjustmentDetailPage = lazy(async () => ({
  default: (await import('@features/adjustments/pages/adjustment-detail.page'))
    .AdjustmentDetailPage,
}));
const QualityPage = lazy(async () => ({
  default: (await import('@features/quality/pages/quality.page')).QualityPage,
}));

const ReportsPage = lazy(async () => ({
  default: (await import('@features/reports/pages/reports.page')).ReportsPage,
}));
const SupplierEvaluationsPage = lazy(async () => ({
  default: (await import('@features/evaluations/pages/supplier-evaluations.page'))
    .SupplierEvaluationsPage,
}));

// --- Almacen (Fase 4) ------------------------------------------------------

const ReceiptsPage = lazy(async () => ({
  default: (await import('@features/receipts/pages/receipts.page')).ReceiptsPage,
}));
const ReceiptDetailPage = lazy(async () => ({
  default: (await import('@features/receipts/pages/receipt-detail.page')).ReceiptDetailPage,
}));
const InventoryPage = lazy(async () => ({
  default: (await import('@features/inventory/pages/inventory.page')).InventoryPage,
}));
const KardexPage = lazy(async () => ({
  default: (await import('@features/inventory/pages/kardex.page')).KardexPage,
}));

// --- Abastecimiento (Fase 3) -----------------------------------------------
const MyApprovalsPage = lazy(async () => ({
  default: (await import('@features/approvals/pages/my-approvals.page')).MyApprovalsPage,
}));

const RequisitionsPage = lazy(async () => ({
  default: (await import('@features/requisitions/pages/requisitions.page')).RequisitionsPage,
}));

const RequisitionDetailPage = lazy(async () => ({
  default: (await import('@features/requisitions/pages/requisition-detail.page'))
    .RequisitionDetailPage,
}));

const QuotationRequestsPage = lazy(async () => ({
  default: (await import('@features/quotations/pages/quotation-requests.page'))
    .QuotationRequestsPage,
}));

const QuotationRequestDetailPage = lazy(async () => ({
  default: (await import('@features/quotations/pages/quotation-request-detail.page'))
    .QuotationRequestDetailPage,
}));

const OrdersPage = lazy(async () => ({
  default: (await import('@features/orders/pages/orders.page')).OrdersPage,
}));

const OrderDetailPage = lazy(async () => ({
  default: (await import('@features/orders/pages/order-detail.page')).OrderDetailPage,
}));

const UsersPage = lazy(async () => ({
  default: (await import('@features/users/pages/users.page')).UsersPage,
}));

const RolesPage = lazy(async () => ({
  default: (await import('@features/roles/pages/roles.page')).RolesPage,
}));

const AuditPage = lazy(async () => ({
  default: (await import('@features/audit/pages/audit.page')).AuditPage,
}));

const ProductsPage = lazy(async () => ({
  default: (await import('@features/products/pages/products.page')).ProductsPage,
}));

const ProductCategoriesPage = lazy(async () => ({
  default: (await import('@features/product-categories/pages/product-categories.page'))
    .ProductCategoriesPage,
}));

const BrandsPage = lazy(async () => ({
  default: (await import('@features/brands/pages/brands.page')).BrandsPage,
}));

const UnitsOfMeasurePage = lazy(async () => ({
  default: (await import('@features/units-of-measure/pages/units-of-measure.page'))
    .UnitsOfMeasurePage,
}));

const WarehousesPage = lazy(async () => ({
  default: (await import('@features/warehouses/pages/warehouses.page')).WarehousesPage,
}));

const SuppliersPage = lazy(async () => ({
  default: (await import('@features/suppliers/pages/suppliers.page')).SuppliersPage,
}));

const DepartmentsPage = lazy(async () => ({
  default: (await import('@features/departments/pages/departments.page')).DepartmentsPage,
}));

const CostCentersPage = lazy(async () => ({
  default: (await import('@features/cost-centers/pages/cost-centers.page')).CostCentersPage,
}));

const ProjectsPage = lazy(async () => ({
  default: (await import('@features/projects/pages/projects.page')).ProjectsPage,
}));

function withSuspense(element: React.ReactNode): React.ReactNode {
  return <Suspense fallback={<LoadingState label="Cargando modulo" />}>{element}</Suspense>;
}

const routes: RouteObject[] = [
  // Unica ruta fuera de la sesion. No usa `AppShell`: la barra lateral y el
  // menu no tienen sentido para quien todavia no ha entrado.
  { path: '/acceso', element: withSuspense(<LoginPage />) },

  {
    element: <RequireAuth />,
    children: [
      // Fuera del armazon a proposito: una sesion con contrasena temporal solo
      // puede cambiarla, asi que ensenarle la navegacion completa seria
      // ofrecerle puertas que el servidor va a cerrar.
      { path: '/cambiar-contrasena', element: withSuspense(<ChangePasswordPage />) },

      {
        path: '/',
        element: <AppShell />,
        children: [
          { index: true, element: withSuspense(<DashboardPage />) },
          { path: 'sistema', element: withSuspense(<SystemStatusPage />) },
          { path: 'diseno', element: withSuspense(<DesignSystemPage />) },

          {
            element: <RequirePermission permissions={['warehouse_transfer.read']} />,
            children: [
              { path: 'transferencias', element: withSuspense(<TransfersPage />) },
              { path: 'transferencias/:id', element: withSuspense(<TransferDetailPage />) },
            ],
          },
          {
            element: <RequirePermission permissions={['dispatch.read']} />,
            children: [
              { path: 'despachos', element: withSuspense(<DispatchesPage />) },
              { path: 'despachos/:id', element: withSuspense(<DispatchDetailPage />) },
            ],
          },

          {
            element: <RequirePermission permissions={['physical_inventory.read']} />,
            children: [
              { path: 'inventario-fisico', element: withSuspense(<CountsPage />) },
              { path: 'inventario-fisico/:id', element: withSuspense(<CountDetailPage />) },
            ],
          },
          {
            element: <RequirePermission permissions={['inventory.adjust']} />,
            children: [
              { path: 'ajustes', element: withSuspense(<AdjustmentsPage />) },
              { path: 'ajustes/:id', element: withSuspense(<AdjustmentDetailPage />) },
            ],
          },
          {
            element: <RequirePermission permissions={['goods_receipt.quality_check']} />,
            children: [{ path: 'calidad', element: withSuspense(<QualityPage />) }],
          },

          {
            element: <RequirePermission permissions={['report.read']} />,
            children: [{ path: 'reportes', element: withSuspense(<ReportsPage />) }],
          },
          {
            element: <RequirePermission permissions={['supplier.read']} />,
            children: [
              {
                path: 'proveedores/:id/evaluaciones',
                element: withSuspense(<SupplierEvaluationsPage />),
              },
            ],
          },

          // --- Almacen (Fase 4) ----------------------------------------------
          {
            element: <RequirePermission permissions={['goods_receipt.read']} />,
            children: [
              { path: 'recepciones', element: withSuspense(<ReceiptsPage />) },
              { path: 'recepciones/:id', element: withSuspense(<ReceiptDetailPage />) },
            ],
          },
          {
            element: <RequirePermission permissions={['inventory.read']} />,
            children: [{ path: 'inventario', element: withSuspense(<InventoryPage />) }],
          },
          {
            element: <RequirePermission permissions={['inventory_movement.read']} />,
            children: [{ path: 'kardex', element: withSuspense(<KardexPage />) }],
          },

          // --- Abastecimiento (Fase 3) ---------------------------------------
          // La ficha cuelga del mismo permiso de lectura que el listado: quien
          // puede ver la lista puede abrir cualquiera de sus documentos.
          {
            element: <RequirePermission permissions={['approval.decide']} />,
            children: [{ path: 'aprobaciones', element: withSuspense(<MyApprovalsPage />) }],
          },
          {
            element: <RequirePermission permissions={['purchase_requisition.read']} />,
            children: [
              { path: 'requerimientos', element: withSuspense(<RequisitionsPage />) },
              { path: 'requerimientos/:id', element: withSuspense(<RequisitionDetailPage />) },
            ],
          },
          {
            element: <RequirePermission permissions={['quotation_request.read']} />,
            children: [
              { path: 'cotizaciones', element: withSuspense(<QuotationRequestsPage />) },
              { path: 'cotizaciones/:id', element: withSuspense(<QuotationRequestDetailPage />) },
            ],
          },
          {
            element: <RequirePermission permissions={['purchase_order.read']} />,
            children: [
              { path: 'ordenes-compra', element: withSuspense(<OrdersPage />) },
              { path: 'ordenes-compra/:id', element: withSuspense(<OrderDetailPage />) },
            ],
          },

          // --- Maestros (Fase 2) ---------------------------------------------
          // Cada uno bajo su propio permiso de lectura: un usuario puede ver
          // el catalogo sin ver a los proveedores.
          {
            element: <RequirePermission permissions={['product.read']} />,
            children: [{ path: 'productos', element: withSuspense(<ProductsPage />) }],
          },
          {
            element: <RequirePermission permissions={['product_category.read']} />,
            children: [{ path: 'categorias', element: withSuspense(<ProductCategoriesPage />) }],
          },
          {
            element: <RequirePermission permissions={['brand.read']} />,
            children: [{ path: 'marcas', element: withSuspense(<BrandsPage />) }],
          },
          {
            element: <RequirePermission permissions={['unit_of_measure.read']} />,
            children: [{ path: 'unidades', element: withSuspense(<UnitsOfMeasurePage />) }],
          },
          {
            element: <RequirePermission permissions={['warehouse.read']} />,
            children: [{ path: 'almacenes', element: withSuspense(<WarehousesPage />) }],
          },
          {
            element: <RequirePermission permissions={['supplier.read']} />,
            children: [{ path: 'proveedores', element: withSuspense(<SuppliersPage />) }],
          },
          {
            element: <RequirePermission permissions={['department.read']} />,
            children: [{ path: 'departamentos', element: withSuspense(<DepartmentsPage />) }],
          },
          {
            element: <RequirePermission permissions={['cost_center.read']} />,
            children: [{ path: 'centros-de-costo', element: withSuspense(<CostCentersPage />) }],
          },
          {
            element: <RequirePermission permissions={['project.read']} />,
            children: [{ path: 'proyectos', element: withSuspense(<ProjectsPage />) }],
          },

          {
            element: <RequirePermission permissions={['user.read']} />,
            children: [{ path: 'usuarios', element: withSuspense(<UsersPage />) }],
          },
          {
            element: <RequirePermission permissions={['role.read']} />,
            children: [{ path: 'roles', element: withSuspense(<RolesPage />) }],
          },
          {
            element: <RequirePermission permissions={['audit.read']} />,
            children: [{ path: 'auditoria', element: withSuspense(<AuditPage />) }],
          },

          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
